import { Controller } from "@hotwired/stimulus"

// The ⓘ details popup (#17764, DESIGN.md A11Y-7) — the HAML twin of React's
// InfoTip `details`. Rendered by TooltipHelper#gl_info_tooltip(text, details:)
// (shared/_info_tip_details): the ⓘ button keeps its gl-tooltip on hover and
// focus, and click / Enter / Space (a native <button>) opens the gl_modal.
//
// The modal ships INERT inside a <template> (so a host that forbids nested
// modal markup — the GOAT Store order dialog, #13905 — can carry the ⓘ), and
// on connect it is mounted on <body>, so it never sits inside a form, a
// settings row or another modal's markup (a .modal nested in a transformed
// ancestor paints in the wrong place). It is found by id first, so a Turbo
// snapshot that restores it already in <body> is reused, never duplicated. Bootstrap closes it on
// Escape, the X, Got It and a backdrop click; focus then returns to the ⓘ.
//
// LAZY mode (opt-in, #18031): a page with hundreds of ⓘ (staff App Settings)
// renders ONE shared popup (TooltipHelper#gl_info_details_shared_modal) and a
// JSON catalog of the details (TooltipHelper#gl_info_details_catalog). ONE
// controller on an ancestor holds the catalog id (gl_info_details_lazy_host_data)
// and each ⓘ carries only its key as an action param
// (TooltipHelper#gl_info_tooltip_lazy). Nothing mounts on connect. On open the shared popup is filled from the
// catalog with textContent (never HTML) and shown, and focus returns to the ⓘ
// that opened it. The shared popup belongs to the page, so a row leaving
// never removes it.
const catalogCache = new WeakMap()

export default class extends Controller {
  static targets = ["modalTemplate"]
  static values = { modalId: String, catalogId: String }

  connect() {
    if (this.lazy) return

    this.modal = document.getElementById(this.modalIdValue) || this.mountFromTemplate()
    if (this.modal && this.modal.parentElement !== document.body) document.body.appendChild(this.modal)

    this.boundReturnFocus = () => this.returnFocus()
    this.modal?.addEventListener("hidden.bs.modal", this.boundReturnFocus)
  }

  disconnect() {
    if (this.lazy || !this.modal) return

    this.modal.removeEventListener("hidden.bs.modal", this.boundReturnFocus)
    // The ⓘ left the page (a frame swap, a removed row): take its popup too.
    if (!this.element.isConnected) {
      window.bootstrap?.Modal?.getInstance?.(this.modal)?.dispose()
      this.modal.remove()
    }
    this.modal = null
  }

  open(event) {
    event?.preventDefault()
    event?.stopPropagation()
    const Modal = window.bootstrap?.Modal
    if (!Modal) return
    if (this.lazy) {
      this.openLazy(Modal, event)
      return
    }
    if (!this.modal) return

    this.trigger = event?.currentTarget || this.element.querySelector("button")
    this.scrollToTop(this.modal)
    Modal.getOrCreateInstance(this.modal).show()
  }

  get lazy() {
    return this.catalogIdValue !== ""
  }

  openLazy(Modal, event) {
    const modal = this.sharedModal()
    const details = this.lookupDetails(event?.params?.key)
    if (!modal || !details) return

    this.fill(modal, details)
    this.scrollToTop(modal)
    this.trigger = event?.currentTarget
    modal.addEventListener("hidden.bs.modal", () => this.returnFocus(), { once: true })
    Modal.getOrCreateInstance(modal).show()
  }

  sharedModal() {
    const modal = document.getElementById(this.modalIdValue)
    if (modal && modal.parentElement !== document.body) document.body.appendChild(modal)
    return modal
  }

  lookupDetails(key) {
    const source = document.getElementById(this.catalogIdValue)
    if (!source) return null

    if (!catalogCache.has(source)) {
      try {
        catalogCache.set(source, JSON.parse(source.textContent || "{}"))
      } catch {
        catalogCache.set(source, {})
      }
    }
    if (key === undefined || key === null) return null
    return catalogCache.get(source)[String(key)] || null
  }

  // The same markup as shared/_info_tip_details' body, built with textContent.
  fill(modal, details) {
    const title = modal.querySelector(".gl-modal-title-text")
    if (title) title.textContent = details.title || ""

    const body = modal.querySelector(".gl-info-details")
    if (!body) return

    const nodes = (details.body || []).map((paragraph) => this.node("p", paragraph))
    const options = details.options || []
    if (options.length) {
      const list = this.node("dl", null, "gl-info-details__options")
      options.forEach((option) => {
        const row = this.node("div", null, "gl-info-details__option")
        row.append(this.node("dt", option.label), this.node("dd", option.text))
        list.append(row)
      })
      nodes.push(list)
    }
    if (details.example) {
      const example = this.node("div", null, "gl-info-details__example")
      example.append(this.node("strong", "Example"), this.node("p", details.example))
      nodes.push(example)
    }
    body.replaceChildren(...nodes)
  }

  // The popup body scrolls (#18107). Both popups are reused — the per-ⓘ one
  // across every open of its ⓘ, the lazy shared one across every ⓘ — so each
  // open starts at the top, never where the last reader left it.
  scrollToTop(modal) {
    const scroller = modal.querySelector(".gl-modal-body")
    if (scroller) scroller.scrollTop = 0
  }

  node(tag, text = null, className = null) {
    const element = document.createElement(tag)
    if (className) element.className = className
    if (text !== null) element.textContent = text ?? ""
    return element
  }

  mountFromTemplate() {
    if (!this.hasModalTemplateTarget) return null

    const content = this.modalTemplateTarget.content.cloneNode(true)
    const modal = content.querySelector(".modal")
    if (modal) document.body.appendChild(modal)
    return modal
  }

  returnFocus() {
    if (this.trigger?.isConnected) this.trigger.focus()
  }
}
